/**
 * Customer-friendly product titles — presentation only.
 * Catalogue matching must continue using brand + model (+ SKU), never displayName.
 */

export type ProductNameParts = {
  brand?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  description?: string | null;
};

export type ResolveProductDisplayNameOptions = {
  /** Retail sales catalogue: prefer stored `name` when displayName absent. */
  preferNameOverBrandModel?: boolean;
  /** Sandbox project catalogue: strip duplicate brand prefix from productName. */
  dedupeBrandPrefix?: boolean;
};

function clean(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function brandFrom(parts: ProductNameParts): string {
  return clean(parts.brand) || clean(parts.manufacturer);
}

/** Technical reference line: "Brand Model" for customer-facing subtitles. */
export function formatProductBrandModelReference(parts: ProductNameParts): string {
  const brand = brandFrom(parts);
  const model = clean(parts.model);
  if (brand && model) return `${brand} ${model}`;
  return brand || model || clean(parts.name) || "";
}

/**
 * Fallback when AI cannot improve: use model (or name) as display name.
 */
export function fallbackProductDisplayName(parts: ProductNameParts): string {
  const model = clean(parts.model);
  if (model) return model;
  const name = clean(parts.name);
  if (name) return name;
  const brandModel = formatProductBrandModelReference(parts);
  if (brandModel) return brandModel;
  // Never promote description into a product title — catalogue normalizer owns that path.
  return "Product";
}

/**
 * Resolve customer-facing display title.
 * Uses persisted displayName when set; otherwise falls back per surface rules.
 */
export function resolveProductDisplayName(
  parts: ProductNameParts,
  options?: ResolveProductDisplayNameOptions,
): string {
  const stored = clean(parts.displayName);
  if (stored) return stored;

  if (options?.preferNameOverBrandModel) {
    const name = clean(parts.name);
    if (name) return name;
  }

  const brandModel = formatProductBrandModelReference(parts);
  if (brandModel && !options?.dedupeBrandPrefix) return brandModel;

  if (options?.dedupeBrandPrefix && brandModel) {
    const brand = brandFrom(parts);
    const productName = clean(parts.name) || clean(parts.model);
    if (brand && productName) {
      const brandLower = brand.toLowerCase();
      const productLower = productName.toLowerCase();
      if (productLower.startsWith(brandLower)) return productName;
      return `${brand} ${productName}`.trim();
    }
    return brandModel;
  }

  return fallbackProductDisplayName(parts);
}

/** Cleo / staff copy: friendly title with technical reference in parentheses. */
export function formatCleoProductReference(parts: ProductNameParts): string {
  const display = resolveProductDisplayName(parts);
  const technical = formatProductBrandModelReference(parts);
  if (!technical || technical === display) return display;
  return `${display}\n(${technical})`;
}

/** Whether AI/sync may overwrite displayName. */
export function canAutoUpdateProductDisplayName(manuallyEdited?: boolean | null): boolean {
  return manuallyEdited !== true;
}

/**
 * Salesperson explicitly accepted a Display Name in the Revise item editor
 * (`Generate AI Display Name` or typing). `displayNameManuallyEdited: true`
 * means staff-approved/protected, not “typed by hand”.
 */
export function acceptStaffApprovedDisplayName(displayName: string): {
  displayName: string;
  displayNameManuallyEdited: true;
} {
  return {
    displayName: clean(displayName),
    displayNameManuallyEdited: true,
  };
}

/** Apply AI suggestion respecting manual-edit lock. */
export function mergeAiProductDisplayName(input: {
  currentDisplayName?: string | null;
  displayNameManuallyEdited?: boolean | null;
  suggestedDisplayName?: string | null;
  brand?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  name?: string | null;
}): { displayName: string; displayNameManuallyEdited: boolean } {
  if (!canAutoUpdateProductDisplayName(input.displayNameManuallyEdited)) {
    return {
      displayName: clean(input.currentDisplayName) || fallbackProductDisplayName(input),
      displayNameManuallyEdited: true,
    };
  }
  const suggested = clean(input.suggestedDisplayName);
  const displayName =
    suggested ||
    clean(input.currentDisplayName) ||
    fallbackProductDisplayName(input);
  return { displayName, displayNameManuallyEdited: false };
}
